import { useState } from "react";
import {
  Accordion,
  AccordionItem,
  Button,
  Card,
  CardBody,
  Tab,
  Tabs,
} from "@heroui/react";
import { IconLogout, IconUserCircle } from "@tabler/icons-react";
import { useAuthStore } from "@/store/auth-store";
import { useViewStore } from "@/store/view-store";
import { useTranslations } from "../../../../i18n";
import { api } from "@/lib/api";
import ProfileBookingsTabs from "./profile-bookings-tabs";
import WidgetPassesPanel from "./profile-passes-panel";
import WidgetPaymentsPanel from "./profile-payments";
import { useBreakpoint } from "@/hooks/use-media-query";
import ProfileAccountTab from "./profile-account-tab";
import ProfileDocumentsPanel from "./profile-documents-panel";

export default function ProfilePage() {
  const t = useTranslations("");
  const navigate = useViewStore((s) => s.navigate);
  const previous = useViewStore((s) => s.previous);
  const profileTab = useViewStore((s) => s.profileTab);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const { isMobile } = useBreakpoint();

  const [selected, setSelected] = useState(profileTab ?? "bookings");

  async function handleLogout() {
    try {
      await api("/api/widget/auth/logout", { method: "POST" });
    } catch {
      // Best-effort logout; clear client state either way.
    }
    logout();
    navigate("login");
  }

  return (
    <div className="p-3 space-y-6">
      <div className="max-w-5xl mx-auto gap-3">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="light"
            color="primary"
            size="md"
            onPress={() => {
              if (previous === "booking") {
                navigate("calendar");
                return;
              }

              navigate(previous || "calendar");
            }}
            startContent={<span aria-hidden="true">←</span>}
          >
            {t("common.back")}
          </Button>

          <div className="flex items-center gap-3">
            {!isMobile && (
              <div className="flex items-center gap-2 text-md text-gray-600">
                <IconUserCircle size={23} className="text-gray-500" />
                <span>{user?.email || "Guest"}</span>
              </div>
            )}
            {isMobile ? (
              <Button
                size="md"
                color="primary"
                variant="ghost"
                onPress={handleLogout}
                isIconOnly
              >
                <IconLogout size={16} />
              </Button>
            ) : (
              <Button
                size="md"
                color="primary"
                variant="ghost"
                onPress={handleLogout}
                startContent={<IconLogout size={16} />}
              >
                {t("common.logout")}
              </Button>
            )}
          </div>
        </div>

        {/* Mobile: accordion */}
        {isMobile ? (
          <Accordion
            variant="splitted"
            onSelectionChange={(keys) => {
              const key = Array.from(keys as Set<string>)[0];
              if (key) setSelected(key);
            }}
          >
            <AccordionItem
              key="bookings"
              title={t("profile.bookingsPage.title")}
            >
              <ProfileBookingsTabs isActive={selected === "bookings"} />
            </AccordionItem>

            <AccordionItem
              key="passes"
              title={t("profile.subscriptions.title")}
            >
              <WidgetPassesPanel isActive={selected === "passes"} />
            </AccordionItem>

            <AccordionItem
              key="payments"
              title={t("profile.paymentsPage.title")}
            >
              <WidgetPaymentsPanel isActive={selected === "payments"} />
            </AccordionItem>

            <AccordionItem
              key="documents"
              title={t("profile.documents.title")}
            >
              <ProfileDocumentsPanel isActive={selected === "documents"} />
            </AccordionItem>

            <AccordionItem key="profile" title="Profile">
              <ProfileAccountTab isActive={selected === "profile"} />
            </AccordionItem>
          </Accordion>
        ) : (
          /* Desktop: tabs */
          <Tabs
            variant="solid"
            className="max-w-5xl mx-auto gap-3"
            color="primary"
            size="lg"
            selectedKey={selected}
            onSelectionChange={(key) => setSelected(key as string)}
          >
            <Tab key="bookings" title={t("profile.bookingsPage.title")}>
              <Card>
                <CardBody>
                  <ProfileBookingsTabs isActive={selected === "bookings"} />
                </CardBody>
              </Card>
            </Tab>

            <Tab key="passes" title={t("profile.subscriptions.title")}>
              <Card>
                <CardBody>
                  <WidgetPassesPanel isActive={selected === "passes"} />
                </CardBody>
              </Card>
            </Tab>

            <Tab key="payments" title={t("profile.paymentsPage.title")}>
              <Card>
                <CardBody>
                  <WidgetPaymentsPanel isActive={selected === "payments"} />
                </CardBody>
              </Card>
            </Tab>

            <Tab key="documents" title={t("profile.documents.title")}>
              <Card>
                <CardBody>
                  <ProfileDocumentsPanel isActive={selected === "documents"} />
                </CardBody>
              </Card>
            </Tab>

            <Tab key="profile" title="Profile">
              <Card>
                <CardBody>
                  <ProfileAccountTab isActive={selected === "profile"} />
                </CardBody>
              </Card>
            </Tab>
          </Tabs>
        )}
      </div>
    </div>
  );
}
